import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { XCircle } from "lucide-react";
import { cancelOrder, listTrades, toastApiError } from "@/lib/api";

const WORKING_ALPACA_STATUSES = new Set([
  "new",
  "accepted",
  "pending_new",
  "partially_filled",
  "pending_cancel",
  "pending_replace",
  "accepted_for_bidding",
  "stopped",
  "suspended",
  "calculated",
  "replaced",
  "done_for_day",
  "held",
]);

export default function OmsPanel({ portfolioId, refreshKey, snapshots, portfolio }) {
  const [trades, setTrades] = useState([]);
  const [engine, setEngine] = useState(null);
  const [cancelingId, setCancelingId] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    if (!portfolioId) return () => {};
    const load = () =>
      listTrades(portfolioId, { limit: 20 })
        .then((r) => {
          if (!alive) return;
          setTrades(r.items || []);
          setEngine(r.engine || null);
        })
        .catch(() => {});
    load();
    const iv = setInterval(load, 5000);
    return () => {
      alive = false;
      clearInterval(iv);
    };
  }, [portfolioId, refreshKey, tick]);

  const sessionPnl = useMemo(() => {
    if (!snapshots || snapshots.length < 2) return { pnl: 0, pct: 0 };
    const first = snapshots[0]?.equity ?? 0;
    const last = snapshots[snapshots.length - 1]?.equity ?? first;
    const pnl = last - first;
    const pct = first > 0 ? (pnl / first) * 100 : 0;
    return { pnl, pct };
  }, [snapshots]);

  const onCancel = async (row) => {
    if (!window.confirm(
      `Cancel this Alpaca order?\n\n${row.side} ${row.quantity} ${row.symbol}\n`
      + `Order ID: ${row.alpaca_order_id}\n\n`
      + `NUMIROCK will send DELETE to Alpaca. If the order has already filled `
      + `at Alpaca, cancellation will be rejected.`
    )) {
      return;
    }
    setCancelingId(row.id);
    try {
      const res = await cancelOrder(portfolioId, row.id);
      toast.success(`Order status: ${res.status || "canceled"}`);
      setTick((t) => t + 1);   // refresh soon
    } catch (err) {
      toastApiError(err);
    } finally {
      setCancelingId(null);
    }
  };

  const isAlpaca = engine === "alpaca";

  return (
    <div data-testid="oms-panel" className="space-y-2">
      <div className="flex items-baseline justify-between">
        <div className="aurion-section-title">
          Execution / OMS
          {isAlpaca ? (
            <span
              data-testid="oms-engine-badge"
              className="ml-2 border border-[var(--aurion-gold)]/40 px-1 text-[9px] uppercase tracking-widest text-[var(--aurion-gold)]"
            >
              Alpaca Paper
            </span>
          ) : null}
        </div>
        <div className="font-mono text-[10px] text-[var(--aurion-muted)]">
          {trades.length} recent
        </div>
      </div>
      {portfolio ? (
        <div
          data-testid="oms-cash-equity"
          className="flex items-baseline justify-between border border-[var(--aurion-border)] bg-[var(--aurion-panel-2)] px-2.5 py-1.5 text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]"
        >
          <span>
            Cash{" "}
            <span className="ml-1 font-mono text-[11px] tabular-nums text-[var(--aurion-text)] normal-case">
              ${Number(portfolio.cash || 0).toLocaleString()}
            </span>
          </span>
          <span>
            Equity{" "}
            <span className="ml-1 font-mono text-[11px] tabular-nums text-[var(--aurion-text)] normal-case">
              ${Number(portfolio.total_equity || 0).toLocaleString()}
            </span>
          </span>
        </div>
      ) : null}
      <div className="border border-[var(--aurion-border)] bg-[var(--aurion-panel-2)] p-3">
        <div className="flex items-baseline justify-between">
          <div className="aurion-section-title">Session P&amp;L</div>
          <div
            data-testid="session-pnl"
            className={`font-mono text-sm ${
              sessionPnl.pnl >= 0
                ? "text-[var(--aurion-green)]"
                : "text-[var(--aurion-red)]"
            }`}
          >
            {sessionPnl.pnl >= 0 ? "+" : ""}${sessionPnl.pnl.toFixed(2)} ·{" "}
            {sessionPnl.pct.toFixed(2)}%
          </div>
        </div>
      </div>
      <div className="aurion-scroll max-h-[220px] space-y-1 overflow-auto">
        {trades.length === 0 ? (
          <div className="border border-dashed border-[var(--aurion-border)] py-4 text-center text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
            no trades yet
          </div>
        ) : (
          trades.map((t) => (
            <OrderRow
              key={t.id}
              row={t}
              isAlpaca={isAlpaca}
              onCancel={onCancel}
              canceling={cancelingId === t.id}
            />
          ))
        )}
      </div>
    </div>
  );
}

function OrderRow({ row, isAlpaca, onCancel, canceling }) {
  const isWorking = isAlpaca && WORKING_ALPACA_STATUSES.has(
    (row.status || "").toLowerCase(),
  );
  const price = row.filled_avg_price != null && row.filled_avg_price > 0
    ? row.filled_avg_price
    : row.price;
  const priceStr = price != null && Number(price) > 0
    ? `$${Number(price).toFixed(2)}`
    : "market";
  const timestamp = row.submitted_at || row.created_at;

  return (
    <div
      data-testid={`oms-trade-${row.id}`}
      className="flex items-baseline justify-between gap-2 border-l-2 border-l-[var(--aurion-border-strong)] bg-[var(--aurion-panel-2)] px-3 py-1.5 text-[11px]"
    >
      <div className="flex flex-1 flex-wrap items-baseline gap-2">
        <span
          className="font-heading text-[10px] uppercase tracking-widest"
          style={{
            color:
              row.side === "BUY"
                ? "var(--aurion-green)"
                : "var(--aurion-red)",
          }}
        >
          {row.side}
        </span>
        <span className="font-mono text-[var(--aurion-text)]">{row.symbol}</span>
        <span className="font-mono text-[var(--aurion-muted-strong)]">
          {row.quantity} × {priceStr}
        </span>
        {row.source && row.source !== "manual" ? (
          <span className="font-mono text-[9px] uppercase tracking-widest text-[var(--aurion-gold)]">
            · {row.source}
          </span>
        ) : null}
        {isAlpaca && row.status ? (
          <span
            data-testid={`oms-order-status-${row.id}`}
            className={`font-mono text-[9px] uppercase tracking-widest ${statusColor(row.status)}`}
          >
            · {row.status.replaceAll("_", " ")}
          </span>
        ) : null}
      </div>
      <div className="flex shrink-0 items-baseline gap-2">
        {isWorking ? (
          <button
            data-testid={`oms-cancel-btn-${row.id}`}
            type="button"
            disabled={canceling}
            onClick={() => onCancel(row)}
            className="flex items-center gap-1 border border-[var(--aurion-red)]/40 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-[var(--aurion-red)] hover:bg-[var(--aurion-red)]/10 disabled:opacity-50"
            title="Cancel this Alpaca order"
          >
            <XCircle className="h-3 w-3" />
            {canceling ? "Canceling…" : "Cancel"}
          </button>
        ) : null}
        <span className="font-mono text-[9px] text-[var(--aurion-muted)]">
          {formatTs(timestamp)}
        </span>
      </div>
    </div>
  );
}

function statusColor(status) {
  const s = (status || "").toLowerCase();
  if (s === "filled") return "text-[var(--aurion-green)]";
  if (s === "rejected") return "text-[var(--aurion-red)]";
  if (s === "canceled" || s === "cancelled" || s === "expired")
    return "text-[var(--aurion-muted-strong)]";
  if (s === "partially_filled") return "text-[var(--aurion-gold)]";
  return "text-[var(--aurion-muted)]";
}

function formatTs(iso) {
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return "";
  }
}
